import React, { useState, useRef, useEffect } from "react";
import { postAIChat } from "../api/client.js";

export default function AIChatModal({ open, onClose, contextTitle }) {
  const initialMessage = contextTitle
    ? `Hi! I’m Home AI. Happy to answer anything about ${contextTitle} or the buying journey.`
    : "Hi! I'm your HOME AI assistant. I can help you with property searches, market insights, valuations, and answer any questions about buying or selling property in the UK. How can I help you today?";

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: initialMessage
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    setMessages([{ role: "assistant", content: initialMessage }]);
    setInput("");
    setError("");
  }, [open, initialMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);
    setError("");

    try {
      const response = await postAIChat(updatedMessages);

      if (response?.error) {
        console.debug("[AIChat] Error response", response);
        if (response.error === "auth_required") {
          setError("Please sign in to chat with Home AI.");
          return;
        }
        setError(
          response.error === "ai_unavailable"
            ? "AI service is temporarily unavailable. Please try again later."
            : response.error === "rate_limit_exceeded"
              ? "Rate limit exceeded. Please wait a moment before trying again."
              : response.message || "AI chat is currently unavailable."
        );
        setMessages(updatedMessages);
      } else if (response?.reply?.content) {
        setMessages([...updatedMessages, { role: "assistant", content: response.reply.content }]);
      } else {
        console.debug("[AIChat] Unexpected response", response);
        setError("Unexpected response format from AI service.");
      }
    } catch (err) {
      console.error("[AIChat] Request failed", err);
      setError(err.message || "Failed to send message. Please try again.");
      setMessages(updatedMessages);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl h-[600px] rounded-3xl bg-slate-950 border border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              Talk to <span className="shine-text font-bold">home</span> AI
            </h2>
            <p className="text-xs text-white/60 mt-1">
              Your intelligent property assistant
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-white/10 transition text-white/70 hover:text-white"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-emerald-500/20 border border-emerald-400/30 text-emerald-100"
                    : "bg-white/5 border border-white/10 text-white/90"
                }`}
              >
                {msg.role === "assistant" && (
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
                    HOME AI
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-white/5 border border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
                  HOME AI
                </div>
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              {error}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-6 border-t border-white/10">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about property..."
              className="flex-1 rounded-2xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400/50 focus:outline-none"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="rounded-2xl bg-emerald-500 px-6 py-3 text-sm font-medium text-white hover:bg-emerald-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
          <p className="text-[10px] text-white/40 mt-2 text-center">
            Powered by advanced AI. Responses may vary and should be verified.
          </p>
        </form>
      </div>
    </div>
  );
}
